/* ============================================================
   FRM Premium Flooring Gallery
   Vanilla-JS port of the React gallery: thumb strip, click-to-
   enlarge hero with hotspots, colour swatches, close-up modal, lightbox.
   Data source: window.FRM_GALLERY (injected by the Liquid snippet
   from product.metafields.custom.gallery, or a single-colour
   fallback built from product images).
   ============================================================ */
(function () {
  "use strict";

  function esc(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function init(root) {
    var DATA = window.FRM_GALLERY;
    if (!Array.isArray(DATA) || DATA.length === 0) return;

    var productTitle = (root && root.dataset && root.dataset.productTitle) || "";

    /* ── Accessible, SEO-meaningful alt text ──
       The gallery metafield an image comes from doesn't require an "alt"
       field to be filled in — most images never will have one. Rather
       than ship a screen reader/crawler a bare colour name or a single
       generic word like "Lifestyle", build a real description from data
       that already exists on every image: the finish name, this
       product's title, and the shot type/room. If a merchant *does* set
       an explicit "alt" on an image object, that always wins. */
    function viewLabel(img) {
      var type = img && img.type;
      var room = (img && img.room ? String(img.room) : "").trim();
      if (type === "lifestyle") {
        return room && room.toLowerCase() !== "lifestyle"
          ? "lifestyle view in the " + room
          : "lifestyle view";
      }
      if (type === "closeup") {
        return "close-up texture view";
      }
      return room ? room.toLowerCase() + " view" : "product view";
    }

    function descriptiveAlt(img, c) {
      if (img && img.alt) return img.alt;
      var bits = [];
      if (c && c.name) bits.push(c.name);
      if (productTitle) bits.push(productTitle);
      var base = bits.join(" ");
      var label = viewLabel(img || {});
      return base ? base + ", " + label : label;
    }

    function swatchAlt(c) {
      var bits = [];
      if (c && c.name) bits.push(c.name);
      if (productTitle) bits.push(productTitle);
      var base = bits.join(" ");
      return base ? base + " colour swatch" : "Colour swatch";
    }

    function closeupAlt(c, idx, total) {
      var bits = [];
      if (c && c.name) bits.push(c.name);
      if (productTitle) bits.push(productTitle);
      var base = bits.join(" ");
      var pos = (typeof idx === "number" && typeof total === "number") ? ", close-up " + (idx + 1) + " of " + total : ", close-up texture view";
      return (base || "Flooring") + pos;
    }

    var state = { colourIdx: 0, imgIdx: 0 };

    function colour() { return DATA[state.colourIdx]; }
    function image() { return colour().images[state.imgIdx]; }

    /* ── Image preloading ──
       The hero <img> is recreated on every navigation, so without
       warming the browser's HTTP cache ahead of time, each click
       triggers a fresh network fetch of a full-size photo — this is
       what caused the multi-second pause after clicking next/prev.
       preloadThen() lets callers wait on a specific image (resolves
       instantly if already cached); the background sweep below keeps
       working through the rest of the gallery during idle time. */
    var preloaded = Object.create(null);
    function preloadThen(src, cb) {
      var called = false;
      function once() { if (called) return; called = true; cb(); }
      if (!src) { once(); return; }
      if (preloaded[src]) { once(); return; }
      var img = new Image();
      img.onload = function () { preloaded[src] = true; once(); };
      img.onerror = once;
      img.src = src;
      if (img.complete) { preloaded[src] = true; once(); }
    }

    function preloadNeighbors() {
      var c = colour();
      [state.imgIdx + 1, state.imgIdx - 1].forEach(function (i) {
        if (i >= 0 && i < c.images.length) preloadThen(c.images[i].src, function () {});
      });
    }

    function preloadAllInBackground() {
      var queue = [];
      var c = colour();
      var n = c.images.length;
      for (var d = 1; d < n; d++) {
        var f = state.imgIdx + d, b = state.imgIdx - d;
        if (f < n) queue.push(c.images[f].src);
        if (b >= 0) queue.push(c.images[b].src);
      }
      DATA.forEach(function (col, i) {
        if (i !== state.colourIdx && col.images[0]) queue.push(col.images[0].src);
      });
      DATA.forEach(function (col, i) {
        if (i === state.colourIdx) return;
        col.images.slice(1).forEach(function (img) { queue.push(img.src); });
      });

      var idx = 0;
      function next() {
        if (idx >= queue.length) return;
        preloadThen(queue[idx++], function () { setTimeout(next, 30); });
      }
      next();
    }

    function schedulePreload() {
      if (window.requestIdleCallback) {
        requestIdleCallback(preloadAllInBackground, { timeout: 2000 });
      } else {
        setTimeout(preloadAllInBackground, 800);
      }
    }

    /* ── Render ── */
    function thumbsHtml(c) {
      return c.images.map(function (img, i) {
        var active = i === state.imgIdx ? " frm-fg__thumb--active" : "";
        var badge = img.type === "lifestyle" ? (img.room || "").split(" ")[0] : "Detail";
        return (
          '<button type="button" class="frm-fg__thumb' + active + '" data-fg-thumb="' + i + '" title="' + esc(img.room) + '">' +
            '<img src="' + esc(img.src) + '" alt="' + esc(descriptiveAlt(img, c)) + '" loading="lazy">' +
            '<span class="frm-fg__thumb-badge">' + esc(badge) + "</span>" +
          "</button>"
        );
      }).join("");
    }

    function hotspotsHtml(img) {
      if (img.type !== "lifestyle" || !Array.isArray(img.hotspots)) return "";
      return img.hotspots.map(function (hs, i) {
        return (
          '<button type="button" class="frm-fg-hotspot" style="left:' + hs.x + '%;top:' + hs.y + '%" ' +
            'data-fg-hotspot="' + i + '" aria-label="Inspect: ' + esc(hs.caption) + '">' +
            '<span class="frm-fg-hotspot__ring"></span><span class="frm-fg-hotspot__dot"></span>' +
          "</button>"
        );
      }).join("");
    }

    function swatchesHtml() {
      return DATA.map(function (c, i) {
        var active = i === state.colourIdx ? " frm-fg__swatch--active" : "";
        return (
          '<button type="button" class="frm-fg__swatch' + active + '" data-fg-swatch="' + i + '" ' +
            'role="radio" aria-checked="' + (i === state.colourIdx) + '" title="' + esc(c.name) + '">' +
            '<span class="frm-fg__swatch-img-wrap"><img src="' + esc(c.swatch) + '" alt="' + esc(swatchAlt(c)) + '" loading="lazy"></span>' +
            '<span class="frm-fg__swatch-name">' + esc(c.name) + "</span>" +
          "</button>"
        );
      }).join("");
    }

    function renderGallery(opts) {
      opts = opts || {};
      var c = colour();
      var img = image();

      var layoutClass = "frm-fg__layout" + (c.images.length > 1 ? "" : " frm-fg__layout--no-thumbs");
      var html =
        '<div class="' + layoutClass + '">' +
          (c.images.length > 1 ? '<div class="frm-fg__thumbs" role="listbox" aria-label="Gallery images">' + thumbsHtml(c) + "</div>" : "") +
          '<div class="frm-fg__hero-col">' +
            '<img class="frm-fg__hero-img" src="' + esc(img.src) + '" alt="' + esc(descriptiveAlt(img, c)) + '" draggable="false">' +
            hotspotsHtml(img) +
            '<div class="frm-fg__hero-badge"><span class="frm-fg__hero-badge-room">' + esc(img.room) + '</span><span class="frm-fg__hero-badge-name">' + esc(c.name) + "</span></div>" +
            '<div class="frm-fg__hero-counter">' + (state.imgIdx + 1) + " / " + c.images.length + "</div>" +
            '<button type="button" class="frm-fg__hero-arr frm-fg__hero-arr--p" data-fg-nav="prev"' + (state.imgIdx === 0 ? " disabled" : "") + ' aria-label="Previous image">&#8249;</button>' +
            '<button type="button" class="frm-fg__hero-arr frm-fg__hero-arr--n" data-fg-nav="next"' + (state.imgIdx === c.images.length - 1 ? " disabled" : "") + ' aria-label="Next image">&#8250;</button>' +
          "</div>" +
        "</div>" +
        (DATA.length > 1 ?
          '<div class="frm-fg__swatches-section">' +
            '<p class="frm-fg__swatches-label">Select Colour</p>' +
            '<div class="frm-fg__swatches" role="radiogroup" aria-label="Flooring colour">' + swatchesHtml() + "</div>" +
          "</div>" : "");

      root.innerHTML = html;

      if (opts.fadeIn) {
        var heroImg = root.querySelector(".frm-fg__hero-img");
        if (heroImg) {
          heroImg.classList.add("frm-fg__hero-img--fade");
          void heroImg.offsetWidth;
          requestAnimationFrame(function () { heroImg.classList.remove("frm-fg__hero-img--fade"); });
        }
      }

      if (c.variantId) {
        document.dispatchEvent(new CustomEvent("frm:gallery:colourchange", {
          detail: { variantId: c.variantId, colour: c }
        }));
      }

      preloadNeighbors();
    }

    /* Fades the current hero image out, waits until the target image is
       actually ready to paint (cached ⇒ near-instant, not yet cached ⇒
       waits on the real load instead of a blind guess), then swaps in
       the new render. The minimum fade floor keeps the crossfade from
       looking like a flash when the image was already preloaded. */
    var HERO_FADE_MS = 220;
    function fadeThenRender(mutate, targetSrc) {
      var heroImg = root.querySelector(".frm-fg__hero-img");
      if (heroImg) heroImg.classList.add("frm-fg__hero-img--fade");
      var start = Date.now();
      preloadThen(targetSrc, function () {
        var wait = Math.max(0, HERO_FADE_MS - (Date.now() - start));
        setTimeout(function () {
          mutate();
          renderGallery({ fadeIn: true });
        }, wait);
      });
    }

    function pickImage(idx) {
      var c = colour();
      if (idx === state.imgIdx || idx < 0 || idx >= c.images.length) return;
      fadeThenRender(function () { state.imgIdx = idx; }, c.images[idx].src);
    }

    function pickColour(idx) {
      if (idx === state.colourIdx || idx < 0 || idx >= DATA.length) return;
      var targetSrc = DATA[idx].images[0] && DATA[idx].images[0].src;
      fadeThenRender(function () { state.colourIdx = idx; state.imgIdx = 0; }, targetSrc);
    }

    function prevImg() { pickImage(state.imgIdx - 1); }
    function nextImg() { pickImage(state.imgIdx + 1); }

    /* ── Close-up modal ── */
    function openModal() {
      var c = colour();
      var closeups = c.closeups || [];
      if (!closeups.length) return;
      var idx = 0;

      var overlay = document.createElement("div");
      overlay.className = "frm-fg-modal";
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      document.body.appendChild(overlay);

      function render() {
        overlay.innerHTML =
          '<div class="frm-fg-modal__box">' +
            '<button type="button" class="frm-fg-modal__close" aria-label="Close">&times;</button>' +
            '<div class="frm-fg-modal__img-wrap"><img class="frm-fg-modal__img" src="' + esc(closeups[idx]) + '" alt="' + esc(closeupAlt(c, idx, closeups.length)) + '"></div>' +
            '<div class="frm-fg-modal__footer">' +
              '<button type="button" class="frm-fg-modal__nav" data-nav="prev"' + (idx === 0 ? " disabled" : "") + ' aria-label="Previous">&#8249;</button>' +
              '<span class="frm-fg-modal__caption">' + esc(c.name) + " · " + (idx + 1) + " / " + closeups.length + "</span>" +
              '<button type="button" class="frm-fg-modal__nav" data-nav="next"' + (idx === closeups.length - 1 ? " disabled" : "") + ' aria-label="Next">&#8250;</button>' +
            "</div>" +
          "</div>";
        [idx + 1, idx - 1].forEach(function (i) {
          if (i >= 0 && i < closeups.length) preloadThen(closeups[i], function () {});
        });
      }

      function close() {
        document.removeEventListener("keydown", onKey);
        overlay.remove();
      }
      function onKey(e) {
        if (e.key === "Escape") close();
        if (e.key === "ArrowRight") { idx = Math.min(idx + 1, closeups.length - 1); render(); }
        if (e.key === "ArrowLeft") { idx = Math.max(idx - 1, 0); render(); }
      }

      overlay.addEventListener("click", function (e) {
        if (e.target === overlay) { close(); return; }
        var nav = e.target.closest("[data-nav]");
        if (nav) {
          idx = nav.dataset.nav === "prev" ? Math.max(idx - 1, 0) : Math.min(idx + 1, closeups.length - 1);
          render();
          return;
        }
        if (e.target.closest(".frm-fg-modal__close")) close();
      });

      document.addEventListener("keydown", onKey);
      render();
    }

    /* ── Fullscreen lightbox ── */
    function openLightbox() {
      var c = colour();
      var images = c.images;
      var idx = state.imgIdx;
      var touchX = null;

      var overlay = document.createElement("div");
      overlay.className = "frm-fg-lb";
      document.body.appendChild(overlay);
      document.body.style.overflow = "hidden";

      function render() {
        overlay.innerHTML =
          '<button type="button" class="frm-fg-lb__close" aria-label="Close lightbox">&times;</button>' +
          '<button type="button" class="frm-fg-lb__nav frm-fg-lb__nav--prev" data-nav="prev" aria-label="Previous">&#8249;</button>' +
          '<div class="frm-fg-lb__img-wrap"><img class="frm-fg-lb__img" src="' + esc(images[idx].src) + '" alt="' + esc(descriptiveAlt(images[idx], c)) + '"></div>' +
          '<button type="button" class="frm-fg-lb__nav frm-fg-lb__nav--next" data-nav="next" aria-label="Next">&#8250;</button>' +
          '<div class="frm-fg-lb__footer"><span class="frm-fg-lb__caption">' + esc(c.name) + " · " + esc(images[idx].room) + '</span><span class="frm-fg-lb__count">' + (idx + 1) + " / " + images.length + "</span></div>" +
          '<div class="frm-fg-lb__dots">' + images.map(function (_, i) {
            return '<button type="button" class="frm-fg-lb__dot' + (i === idx ? " frm-fg-lb__dot--active" : "") + '" data-dot="' + i + '" aria-label="Image ' + (i + 1) + '"></button>';
          }).join("") + "</div>";
        [(idx + 1) % images.length, (idx - 1 + images.length) % images.length].forEach(function (i) {
          preloadThen(images[i].src, function () {});
        });
      }

      function close() {
        document.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
        overlay.remove();
      }
      function onKey(e) {
        if (e.key === "Escape") close();
        if (e.key === "ArrowRight") { idx = (idx + 1) % images.length; render(); }
        if (e.key === "ArrowLeft") { idx = (idx - 1 + images.length) % images.length; render(); }
      }

      overlay.addEventListener("click", function (e) {
        var nav = e.target.closest("[data-nav]");
        if (nav) { idx = nav.dataset.nav === "prev" ? (idx - 1 + images.length) % images.length : (idx + 1) % images.length; render(); return; }
        var dot = e.target.closest("[data-dot]");
        if (dot) { idx = parseInt(dot.dataset.dot, 10); render(); return; }
        if (e.target.closest(".frm-fg-lb__close")) close();
      });
      overlay.addEventListener("touchstart", function (e) { touchX = e.touches[0].clientX; });
      overlay.addEventListener("touchend", function (e) {
        if (touchX === null) return;
        var d = touchX - e.changedTouches[0].clientX;
        if (Math.abs(d) > 50) { idx = d > 0 ? (idx + 1) % images.length : (idx - 1 + images.length) % images.length; render(); }
        touchX = null;
      });

      document.addEventListener("keydown", onKey);
      render();
    }

    /* ── Delegated events ── */
    root.addEventListener("click", function (e) {
      var nav = e.target.closest("[data-fg-nav]");
      if (nav) { nav.dataset.fgNav === "prev" ? prevImg() : nextImg(); return; }

      var thumb = e.target.closest("[data-fg-thumb]");
      if (thumb) { pickImage(parseInt(thumb.dataset.fgThumb, 10)); return; }

      var swatch = e.target.closest("[data-fg-swatch]");
      if (swatch) { pickColour(parseInt(swatch.dataset.fgSwatch, 10)); return; }

      var hotspot = e.target.closest("[data-fg-hotspot]");
      if (hotspot) { openModal(); return; }

      /* Click anywhere else on the hero (the lifestyle image itself,
         badge, counter) opens the fullscreen lightbox. */
      if (e.target.closest(".frm-fg__hero-col")) { openLightbox(); return; }
    });

    root.addEventListener("keydown", function (e) {
      var swatch = e.target.closest("[data-fg-swatch]");
      if (!swatch) return;
      var idx = parseInt(swatch.dataset.fgSwatch, 10);
      if (e.key === "ArrowRight") { e.preventDefault(); pickColour(Math.min(idx + 1, DATA.length - 1)); }
      if (e.key === "ArrowLeft") { e.preventDefault(); pickColour(Math.max(idx - 1, 0)); }
    });

    renderGallery();
    schedulePreload();

    /* Public API so other on-page controls (e.g. an explicit colour
       selector next to the calculator) can drive the same gallery
       state instead of re-implementing colour switching. */
    window.FRM_GALLERY_API = {
      select: pickColour,
      getData: function () { return DATA; },
      getColourIdx: function () { return state.colourIdx; }
    };
  }

  function boot() {
    var root = document.getElementById("frm-fg-root");
    if (root) init(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
